import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A shelled village house — WWI battlefield scenery, not a staged building.
// Broken plaster walls of differing heights with jagged, stepped tops; one
// gable corner still standing at the back-left with a clinging fragment of
// terracotta roof; a rubble heap spilling out of the doorway into the yard;
// charred timber beams leaning where the roof used to be. Authored footprint-
// native for a 2x2 tile lot, centred on the origin, base at y = 0.
const PLASTER = "#e3d5b8";       // sunlit broken plaster
const PLASTER_DARK = "#cbbb9c";  // shaded / weathered plaster
const TERRACOTTA = "#b0563a";    // surviving roof tiles
const CHARRED = "#3a322c";       // burnt timber
const RUBBLE = "#a89c85";        // plaster/stone debris
const RUBBLE_DARK = "#8d816c";   // darker debris
const FLOOR = "#b7ab92";         // debris-strewn interior floor

const T = 0.08; // wall thickness

// A wall segment: a box standing on the ground, its top the jagged break line.
function wallX(cx: number, w: number, h: number, z: number, color = PLASTER): Part {
  // A segment of a wall running along X (front/back walls).
  return { geometry: new THREE.BoxGeometry(w, h, T), color, position: [cx, h / 2, z] };
}
function wallZ(cz: number, d: number, h: number, x: number, color = PLASTER): Part {
  // A segment of a wall running along Z (side walls).
  return { geometry: new THREE.BoxGeometry(T, h, d), color, position: [x, h / 2, cz] };
}

function rubbleChunk(x: number, z: number, r: number, y: number, color: string, squash = 0.6): Part {
  return { geometry: new THREE.IcosahedronGeometry(r, 0), color, position: [x, y, z], scale: [1.15, squash, 1] };
}

export const ruinedHouse = defineModel({
  id: "ruined-house",
  batchId: "wwi",
  author: "Fable 5",
  name: "Ruined House",
  tags: ["wwi", "scenery", "ruin", "building", "destroyed", "village", "rubble"],
  summary: "A shelled village house — jagged plaster walls, one gable corner with a roof fragment, rubble and charred beams.",
  description:
    "Battlefield scenery of a shelled house: broken plaster walls stepping down to " +
    "stumps, the back-left gable corner still standing under a fragment of " +
    "terracotta roof, a rubble heap spilling through the doorway into the yard and " +
    "charred beams leaning where the roof fell. A single-variant scenery piece, " +
    "not a staged building.",
  footprint: 1.05,
  tiles: { w: 2, h: 2 },
  parts(): Part[] {
    return [
      // Debris-strewn interior floor slab.
      { geometry: new THREE.BoxGeometry(1.32, 0.05, 1.32), color: FLOOR, position: [0, 0.025, 0] },

      // ── Back wall (z = -0.66), stepping down left -> right ──
      wallX(-0.45, 0.5, 0.78, -0.66),
      wallX(-0.02, 0.42, 0.5, -0.66, PLASTER_DARK),
      wallX(0.45, 0.5, 0.3, -0.66),

      // ── Left wall (x = -0.66), tall at the gable corner, crumbling forward ──
      wallZ(-0.47, 0.46, 0.78, -0.66, PLASTER_DARK),
      wallZ(-0.02, 0.44, 0.46, -0.66),
      wallZ(0.45, 0.5, 0.22, -0.66, PLASTER_DARK),

      // ── Half-gable rising to the back-left corner peak ──
      { geometry: new THREE.BoxGeometry(0.36, 0.15, T), color: PLASTER, position: [-0.52, 0.855, -0.66] },
      { geometry: new THREE.BoxGeometry(0.24, 0.13, T), color: PLASTER_DARK, position: [-0.58, 0.99, -0.66] },
      { geometry: new THREE.BoxGeometry(0.13, 0.1, T), color: PLASTER, position: [-0.635, 1.1, -0.66] },

      // ── Terracotta roof fragment clinging to the gable corner ──
      // The main plate slopes down from the gable peak toward +x, hugging the
      // back wall; a broken purlin carries its inner edge.
      { geometry: new THREE.BoxGeometry(0.5, 0.04, 0.34), color: TERRACOTTA,
        position: [-0.44, 0.97, -0.5], rotation: [0, 0, -0.5] },
      // A slid-down plate resting on the back wall's middle break.
      { geometry: new THREE.BoxGeometry(0.3, 0.035, 0.26), color: TERRACOTTA,
        position: [-0.06, 0.53, -0.56], rotation: [0, 0, -0.35] },
      // Charred purlin jutting out of the gable under the roof plate.
      { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.52), color: CHARRED,
        position: [-0.36, 0.88, -0.42], rotation: [-0.08, 0, 0] },

      // ── Front wall (z = +0.66) with the doorway gap ──
      wallX(-0.5, 0.4, 0.32, 0.66),
      { geometry: new THREE.BoxGeometry(0.12, 0.52, T), color: PLASTER_DARK, position: [-0.24, 0.26, 0.66] }, // door jamb stub
      wallX(0.34, 0.72, 0.2, 0.66, PLASTER_DARK),

      // ── Right wall (x = +0.66), reduced to stumps ──
      wallZ(-0.34, 0.72, 0.28, 0.66),
      wallZ(0.36, 0.68, 0.15, 0.66, PLASTER_DARK),

      // ── Rubble heap spilling through the doorway into the yard ──
      rubbleChunk(0.1, 0.68, 0.2, 0.1, RUBBLE),
      rubbleChunk(0.32, 0.84, 0.15, 0.08, RUBBLE_DARK),
      rubbleChunk(-0.08, 0.88, 0.13, 0.06, RUBBLE),
      rubbleChunk(0.14, 0.95, 0.1, 0.05, RUBBLE_DARK, 0.7),
      rubbleChunk(0.3, 0.4, 0.16, 0.08, RUBBLE_DARK),
      rubbleChunk(-0.25, 0.35, 0.12, 0.06, RUBBLE),
      // Brick and plaster chunks in the heap.
      { geometry: new THREE.BoxGeometry(0.12, 0.07, 0.09), color: TERRACOTTA, position: [0.44, 0.05, 0.72], rotation: [0, 0.5, 0] },
      { geometry: new THREE.BoxGeometry(0.09, 0.06, 0.13), color: PLASTER, position: [-0.05, 0.05, 0.78], rotation: [0, -0.35, 0.1] },
      { geometry: new THREE.BoxGeometry(0.1, 0.05, 0.08), color: TERRACOTTA, position: [0.2, 0.04, 0.55], rotation: [0, 0.9, 0] },

      // ── Charred timber beams ──
      // One leaning from the floor up against the tall back wall segment.
      { geometry: new THREE.BoxGeometry(0.07, 0.07, 0.9), color: CHARRED,
        position: [-0.28, 0.36, -0.28], rotation: [0.85, 0, 0] },
      // One collapsed across the rubble in the yard.
      { geometry: new THREE.BoxGeometry(0.06, 0.06, 0.7), color: CHARRED,
        position: [0.42, 0.13, 0.6], rotation: [0.12, 0.55, 0] },
    ];
  },
  animations: [],
});
